/**
 * URL-based State Management System for TanStack Router
 *
 * This system treats the URL as the single source of truth for all persistent application state.
 * Adapted from the original React Router implementation to work with TanStack Router.
 */

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

// Type definitions based on the original React Native AppUiState
export interface UrlState {
	// Transaction data
	token?: string;
	transactionId?: string;

	// User flow control
	operation?: "withdraw" | "deposit";
	method?: "cash" | "bank";
	flow?: string;

	// Form completion tracking
	location?: boolean;
	basicInfo?: boolean;
	addressInfo?: boolean;

	// User data
	name?: string;
	email?: string;
	phone?: string;

	// Location information
	country?: string; // DEPRECATED: Legacy field for backward compatibility
	residency_country?: string; // Where user lives (for address)
	payout_country?: string; // Where user wants to receive money
	country_src?: "manual" | "url" | "profile" | "service"; // Source of payout_country
	locks?: string; // Comma-separated list of locked fields
	state?: string; // State/province code (e.g., 'IL', 'CA', 'TX')
	address?: string;
	city?: string;
	zipCode?: string;

	// Internationalization
	lang?: "en" | "es";

	// Calculation data
	amount?: string;
	currency?: string; // Source currency (e.g., 'USDC', 'USD')
	targetCurrency?: string; // Target currency for exchange
	fees?: string;
	total?: string;
	exchangeRate?: string;

	// Navigation state (derived from other params)
	currentPage?: "calculator" | "phone" | "name" | "address" | "review";
}

// Type-safe parameter keys
export type UrlStateKey = keyof UrlState;

// URL parameter serialization helpers
const serializeValue = (value: unknown): string => {
	if (typeof value === "boolean") {
		return value ? "true" : "false";
	}
	return String(value);
};

const deserializeValue = (value: string, key: UrlStateKey): unknown => {
	// Boolean fields
	if (["location", "basicInfo", "addressInfo"].includes(key)) {
		return value === "true";
	}

	// Keep as string for all other fields
	return value;
};

/**
 * Custom hook for URL-based state management with TanStack Router
 * Provides type-safe access to URL search parameters as application state
 */
export function useUrlState() {
	const search = useSearch({ from: "__root__" });
	const navigate = useNavigate();

	// Get current state from URL search parameters
	const state = useMemo((): UrlState => {
		const urlState: UrlState = {};

		// TanStack Router provides search params as an object
		for (const [key, value] of Object.entries(search || {})) {
			if (value !== undefined && value !== null && value !== "") {
				// biome-ignore lint/suspicious/noExplicitAny: Dynamic key assignment requires flexible typing
				(urlState as any)[key] = deserializeValue(
					String(value),
					key as UrlStateKey,
				);
			}
		}

		return urlState;
	}, [search]);

	// Update state by modifying URL parameters
	const setState = useCallback(
		(updates: Partial<UrlState>, replace = false) => {
			const newSearch = { ...search } as Record<string, string | boolean>;

			// Apply updates
			for (const [key, value] of Object.entries(updates)) {
				if (value === undefined || value === null || value === "") {
					delete newSearch[key];
				} else {
					newSearch[key] = serializeValue(value);
				}
			}

			navigate({
				// biome-ignore lint/suspicious/noExplicitAny: TanStack Router requires flexible search param type
				search: newSearch as any,
				replace,
			});
		},
		[search, navigate],
	);

	// Navigate to a new route while preserving state
	const navigateWithState = useCallback(
		(to: string, additionalState?: Partial<UrlState>) => {
			const newSearch = { ...search } as Record<string, string | boolean>;

			if (additionalState) {
				for (const [key, value] of Object.entries(additionalState)) {
					if (value === undefined || value === null || value === "") {
						delete newSearch[key];
					} else {
						newSearch[key] = serializeValue(value);
					}
				}
			}

			navigate({
				to,
				// biome-ignore lint/suspicious/noExplicitAny: TanStack Router requires flexible search param type
				search: newSearch as any,
			});
		},
		[navigate, search],
	);

	// Clear all state (reset to empty search)
	const clearState = useCallback(() => {
		navigate({
			// biome-ignore lint/suspicious/noExplicitAny: TanStack Router requires flexible search param type
			search: {} as any,
			replace: true,
		});
	}, [navigate]);

	return {
		state,
		setState,
		navigateWithState,
		clearState,
	};
}

/**
 * Hook for specific URL state values with type safety
 */
export function useUrlStateValue<K extends UrlStateKey>(
	key: K,
	defaultValue?: UrlState[K],
): [UrlState[K], (value: UrlState[K]) => void] {
	const { state, setState } = useUrlState();

	const value = state[key] ?? defaultValue;

	const setValue = useCallback(
		(newValue: UrlState[K]) => {
			setState({ [key]: newValue });
		},
		[key, setState],
	);

	return [value, setValue];
}

/**
 * Quick flow redirect logic
 * Determines where users should be redirected based on existing data in URL
 */
export function useQuickFlowRedirect() {
	const { state, navigateWithState } = useUrlState();

	const getRedirectPath = useCallback((): string => {
		// Check if user has existing transaction data
		const hasBasicInfo = state.name && state.email;
		const hasAddressInfo = state.address && state.city && state.state;
		const hasAmount = state.amount;

		// Existing users with complete data go to review
		if (hasBasicInfo && hasAddressInfo && hasAmount) {
			return "/ramps/review";
		}

		// Users with some data continue from where they left off
		if (hasBasicInfo && !hasAddressInfo) {
			return "/ramps/address";
		}

		if (hasAmount && !hasBasicInfo) {
			return "/ramps/phone";
		}

		// New users start at calculator
		return "/ramps/calculator";
	}, [state]);

	const performRedirect = useCallback(() => {
		const path = getRedirectPath();
		navigateWithState(path);
	}, [getRedirectPath, navigateWithState]);

	return {
		getRedirectPath,
		performRedirect,
		isExistingUser: Boolean(state.name && state.email && state.amount),
	};
}

/**
 * Form completion tracking
 * Tracks which steps of the form have been completed
 */
export function useFormProgress() {
	const { state } = useUrlState();

	const progress = useMemo(() => {
		const steps = [
			{ key: "calculator", completed: Boolean(state.amount), required: true },
			{
				key: "phone",
				completed: Boolean(state.phone),
				required: true,
			},
			{
				key: "name",
				completed: Boolean(state.name),
				required: true,
			},
			{
				key: "address",
				completed: Boolean(state.address && state.city && state.state),
				required: state.operation === "withdraw",
			},
			{ key: "review", completed: false, required: false }, // Always last step
		];

		const completedSteps = steps.filter((step) => step.completed).length;
		const requiredSteps = steps.filter((step) => step.required).length;
		const percentage =
			requiredSteps > 0 ? (completedSteps / requiredSteps) * 100 : 0;

		return {
			steps,
			completedSteps,
			totalSteps: steps.length,
			requiredSteps,
			percentage: Math.min(percentage, 100),
			isComplete: completedSteps >= requiredSteps,
		};
	}, [state]);

	return progress;
}

/**
 * Check if iOS WebKit message handlers are available
 * Only returns true if:
 * 1. Running on iOS device (iPhone/iPad/iPod)
 * 2. In a WebView (not Safari)
 * 3. webkit.messageHandlers exists
 * 4. ReactNativeWebView is NOT present (to avoid conflicts)
 */
function isIOSWebKitAvailable(): boolean {
	if (typeof window === "undefined") return false;

	// Check if ReactNativeWebView exists first - if it does, use that instead
	// biome-ignore lint/suspicious/noExplicitAny: ReactNativeWebView is dynamically injected
	if ((window as any).ReactNativeWebView) {
		return false; // Prefer ReactNativeWebView over native iOS handlers
	}

	const userAgent = navigator.userAgent || "";
	const isIOSDevice = /iPhone|iPad|iPod/.test(userAgent);
	const isWebView = /WebView/.test(userAgent);
	const hasWebKitHandlers =
		typeof window.webkit !== "undefined" &&
		typeof window.webkit.messageHandlers !== "undefined";

	return isIOSDevice && isWebView && hasWebKitHandlers;
}

/**
 * PostMessage integration for WebView communication
 * Sends state updates to parent WebView when data changes
 *
 * Supports multiple WebView environments:
 * 1. React Native WebView (priority - checked first)
 * 2. iOS WebKit message handlers (native iOS WKWebView)
 * 3. Parent frame postMessage (web embedding)
 * 4. Custom events (internal listeners)
 */
export function useWebViewIntegration() {
	const { state } = useUrlState();

	const sendToWebView = useCallback(
		(eventType: string, data?: unknown) => {
			const message = {
				type: eventType,
				payload: data || state,
				timestamp: Date.now(),
			};

			if (typeof window === "undefined") return;

			// IMPORTANT: Check React Native WebView FIRST (it can also run on iOS)
			// React Native WebView takes precedence over native iOS WebKit handlers
			// biome-ignore lint/suspicious/noExplicitAny: ReactNativeWebView is dynamically injected
			const reactNativeWebView = (window as any).ReactNativeWebView;
			if (reactNativeWebView) {
				try {
					reactNativeWebView.postMessage(JSON.stringify(message));
				} catch (error) {
					// Silently fail - don't break app if ReactNativeWebView has issues
					if (process.env.NODE_ENV === "development") {
						console.warn(
							"[WebView] Failed to send message to ReactNativeWebView:",
							error,
						);
					}
				}
			}

			// iOS WebKit Message Handler - only if ReactNativeWebView is NOT present
			// This ensures backward compatibility - React Native apps continue to work
			if (!reactNativeWebView && isIOSWebKitAvailable()) {
				try {
					const handlerName = "rampsUI"; // Configurable via env var if needed
					const handler = window.webkit?.messageHandlers?.[handlerName];
					if (handler) {
						handler.postMessage(JSON.stringify(message));
					}
				} catch (error) {
					// Silently fail - don't break app if iOS handler has issues
					if (process.env.NODE_ENV === "development") {
						console.warn(
							"[WebView] Failed to send message to iOS WebKit handler:",
							error,
						);
					}
				}
			}

			// Send to parent frame (for web embedding)
			if (window.parent !== window) {
				try {
					window.parent.postMessage(message, "*");
				} catch (error) {
					// Silently fail - cross-origin restrictions may prevent this
					if (process.env.NODE_ENV === "development") {
						console.warn(
							"[WebView] Failed to send message to parent frame:",
							error,
						);
					}
				}
			}

			// Custom event for internal listeners
			try {
				window.dispatchEvent(
					new CustomEvent("pwa-state-change", { detail: message }),
				);
			} catch (error) {
				// This should never fail, but handle gracefully just in case
				if (process.env.NODE_ENV === "development") {
					console.warn("[WebView] Failed to dispatch custom event:", error);
				}
			}
		},
		[state],
	);

	return { sendToWebView };
}
